"use client";

import React from "react";
import { CircleCheck, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
const steps = [{
  title: "Anfrage & Beratung",
  description: "Teilen Sie uns Ihren Bedarf mit. Wir beraten Sie zur optimalen Bechermenge und Logistik für Ihr Event."
}, {
  title: "Planung & Logistik",
  description: "Wir erstellen ein maßgeschneidertes Angebot inklusive Lieferung, Reinigung und optionaler Lagerung."
}, {
  title: "Event & Rückgabe",
  description: "Nach Ihrer erfolgreichen Veranstaltung holen wir die Becher ab und kümmern uns um die professionelle Reinigung."
}];
const budgetOptions = ["Bis 500 Becher", "500 - 2.000 Becher", "2.000 - 10.000 Becher", "Über 10.000 Becher"];
function ProjectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="234523" className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-6 flex size-16 items-center justify-center rounded-full bg-primary/20 text-primary">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="mb-2 font-default text-2xl font-semibold text-foreground">Anfrage erfolgreich gesendet</h3>
        <p className="mb-8 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-border hover:bg-accent rounded-full">
          Weitere Anfrage senden
        </Button>
      </div>;
  }
  const inputBaseStyles = "flex w-full border-0 border-b border-border bg-transparent py-3 text-base transition-colors placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name ist erforderlich"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Ihr Name</FormLabel>
              <FormControl>
                <input type="text" placeholder="Max Mustermann" className={inputBaseStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-Mail ist erforderlich",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte geben Sie eine gültige E-Mail-Adresse ein"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">E-Mail Adresse</FormLabel>
              <FormControl>
                <input type="email" placeholder="max@beispiel.at" className={inputBaseStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Organisation / Firma</FormLabel>
              <FormControl>
                <input type="text" placeholder="Event GmbH" className={inputBaseStyles} {...field} />
              </FormControl>
              <FormDescription className="text-xs">Optional für Vereine oder Firmen</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Telefonnummer</FormLabel>
              <FormControl>
                <input type="tel" placeholder="+43 664 1234567" className={inputBaseStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <FormField control={control} name="budget" rules={{
        required: "Bitte wählen Sie die geschätzte Menge"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Bechermenge</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="border-x-0 border-t-0 border-b border-border rounded-none bg-transparent px-0 py-3 text-base shadow-none focus-visible:ring-0 focus-visible:border-primary data-[state=open]:border-primary">
                    <SelectValue placeholder="Menge auswählen" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {budgetOptions.map((option, i) => <SelectItem key={i} data-index={i} value={option}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="start_date" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Eventdatum</FormLabel>
              <FormControl>
                <input type="date" className={`${inputBaseStyles} [color-scheme:light]`} {...field} />
              </FormControl>
              <FormDescription className="text-xs">Wann findet Ihre Veranstaltung statt?</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="description" rules={{
      required: "Nachricht ist erforderlich"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-semibold">Ihre Nachricht</FormLabel>
            <FormControl>
              <textarea placeholder="Details zum Event, Ort oder speziellen Wünschen (z.B. Reinigung, Druck)" className={`${inputBaseStyles} min-h-[100px] resize-y`} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto group rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg transition-all hover:scale-105">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Wird gesendet...
            </> : <>
              Jetzt Anfrage senden
              <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function StartAProject() {
  return <section id="schreiben-sie-uns" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#16a34a08_1px,transparent_1px),linear-gradient(to_bottom,#16a34a08_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none"></div>

      <div className="container mx-auto px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 lg:gap-24">
          
          {}
          <div className="flex flex-col justify-center">
            <div className="inline-flex items-center rounded-full border border-primary/30 bg-accent px-3 py-1 text-sm font-medium text-primary mb-6 w-fit">
              <span className="flex size-2 rounded-full bg-primary mr-2 animate-pulse"></span>
              Regional & Nachhaltig
            </div>
            
            <h2 className="font-default text-4xl md:text-5xl font-bold tracking-tight mb-6 text-foreground">
              Schreiben Sie uns
            </h2>
            
            <p className="text-lg text-muted-foreground mb-12 max-w-md leading-relaxed font-default">
              Starten Sie Ihre nachhaltige Veranstaltung mit Cup4us. Wir bieten Ihnen saubere Lösungen für Mehrwegbecher in ganz Kärnten – unkompliziert und professionell.
            </p>

            <div className="space-y-8">
              <h3 className="font-default text-xl font-semibold text-foreground border-b border-border pb-4 uppercase tracking-wider">
                Der Ablauf
              </h3>
              <ul className="space-y-8">
                {steps.map((step, i) => <li key={i} data-index={i} className="flex gap-5 group">
                    <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-accent text-primary transition-all group-hover:bg-primary group-hover:text-primary-foreground group-hover:scale-110">
                      <span className="font-mono text-sm font-medium">{i + 1}</span>
                    </div>
                    <div>
                      <h4 className="font-default text-base font-semibold text-foreground mb-1">
                        {step.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed font-default">
                        {step.description}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div className="bg-card backdrop-blur-sm border border-border rounded-2xl p-8 md:p-10 shadow-xl shadow-secondary/5 relative">
            {}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-primary/30 rounded-tl-2xl pointer-events-none"></div>
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-primary/30 rounded-br-2xl pointer-events-none"></div>
            
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Schreiben Sie uns" defaultValues={{
            name: "",
            email: "",
            company_name: "",
            phone: "",
            budget: "",
            description: "",
            start_date: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            budget: {
              required: true
            },
            description: {
              required: true
            }
          }} submitText="Jetzt Anfrage senden" successMessage="Vielen Dank für Ihre Nachricht! Wir haben Ihre Anfrage erhalten und unser Team wird sich in Kürze mit einem individuellen Angebot bei Ihnen melden.">
              <ProjectForm />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}